// MACHINERY V2 (Stroje) — the add-from-catalog form's data + behaviour (M2.8).
//
// Spec: data package §11, the `prefill.*` half of the filter/prefill table.
// Reference impl: components/MachineDialog.tsx `handleCreateMachine`, its
// CATALOG branch (:335-348). Artboard 0903.
//
// >>> §9 D7 / RULE (d) — THE DIALOG NEVER EATS THE USER'S WORK. <<<
// v1 calls `onClose()` at MachineDialog.tsx:370, BEFORE
// `await dispatch(createMachine(model)).unwrap()` on :371 — so a rejection
// lands in the slice with the form already unmounted, and the serial number the
// user just typed is gone. Here the create is awaited FIRST and `onCreated`
// (the ONLY close path) runs inside the success branch. The catch touches NO
// form value: it sets `submitError` and returns, so serialNumber, productionYear
// and locationId are all still on screen with a retry. Structurally identical
// to add/useCustomMachineForm.ts — that file is D7's own sub-phase and this is
// the same contract, not a second implementation of it.
//
// >>> THE THREE EDITABLE FIELDS, AND WHY EVERYTHING ELSE IS `undefined`. <<<
// §11: only serialNumber (required, max 50), productionYear (4 digits,
// 1900..current) and locationId are the user's. `catalogModelId` is the
// linchpin — the backend's `ResolveCatalogFieldsAsync` (Phase 7E) re-derives
// manufacturer, operation type, axis config, control-system brand and the
// flattened Phase-7F specs FROM it. Sending `manufacturerId` ourselves caused
// 400s (§11: "NOT used for prefill (Phase 7E; caused 400s)"), so every
// classification id is forced `undefined`, exactly as v1's catalog branch does.
//
// >>> NEVER SEND AN EMPTY STRING FOR A GUID. <<<
// `''` is not a Guid and the API rejects it. `locationId` therefore publishes
// `undefined` when nothing is picked, and no id field here is ever seeded with
// `""` in the first place — unlike the custom form, which has to keep v1's
// empty-string defaults for its own disabled-field comparison.
//
// TYPE IS FORCED CNC (v1 :338-339). The catalog is a CNC catalog; if the type
// list has not loaded yet the field is left undefined rather than guessed, and
// the backend resolver fills it from the catalog model anyway.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useCallback, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { CatalogModelListItem, ChangeMachine, MachineTypes } from "../../models/models";
import { createMachine, getTypes, setError } from "../../customerMachinerySlice";
import { fetchLocations } from "../../customerLocationsSlice";
import { toastErrorMessage } from "../feedback/MachineryToastHost";

/**
 * The form is typed `ChangeMachine` even though only two of its fields are
 * REGISTERED, so the dialog can reuse M2.7's `TextRow` / `TwoCol` atoms
 * unchanged (they are typed to `FieldPath<ChangeMachine>`). A second field kit
 * for two inputs would be the duplication the atoms exist to prevent, and it
 * matches v1, whose catalog mode is the same `ChangeMachine` form in a reduced
 * rendering. Everything not registered here is built at submit and never reads
 * back from RHF.
 */
const DEFAULTS: ChangeMachine = {
  model: "",
  serialNumber: "",
  productionYear: undefined,
  image: null,
};

export const useCatalogAddForm = (
  open: boolean,
  model: CatalogModelListItem,
  onCreated: (id?: string) => void
) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation(["myMachinery", "common"]);

  const types = useAppSelector((s) => s.customerMachinery.types);
  const locations = useAppSelector((s) => s.customerLocations.locations);

  const form = useForm<ChangeMachine>({ defaultValues: DEFAULTS });
  const { reset } = form;

  /** Outside RHF, exactly as v1 keeps it (MachineDialog.tsx:152). */
  const [locationId, setLocationId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  // The two lists this dialog cannot submit without. Mirrors the custom
  // dialog's bootstrap (add/useCustomMachineForm.ts) — the catalog pages are an
  // entry point that never loaded either.
  useEffect(() => {
    if (!open) return;
    if (types.length === 0) dispatch(getTypes());
    if (locations.length === 0) dispatch(fetchLocations());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dispatch]);

  const resetAll = useCallback(() => {
    reset(DEFAULTS);
    setLocationId(null);
    setSubmitError(undefined);
  }, [reset]);

  /** A different model selected -> start clean, so a serial typed for model A
   *  can never be submitted against model B. */
  useEffect(() => {
    resetAll();
  }, [model.id, resetAll]);

  const onSubmit: SubmitHandler<ChangeMachine> = async (data) => {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(undefined);

    const cncType = types.find((t) => t.name === MachineTypes.cnc);

    const payload: ChangeMachine = {
      model: model.modelName,
      serialNumber: data.serialNumber,
      catalogModelId: model.id,
      typeId: cncType ? cncType.id : undefined,
      locationId: locationId || undefined,
      productionYear: data.productionYear ? data.productionYear : undefined,
      // Phase 7E — the backend re-derives every one of these from
      // catalogModelId. Sending manufacturerId is what produced the 400s.
      manufacturerId: undefined,
      operationTypeId: undefined,
      axisConfigurationId: undefined,
      controlSystemBrandId: undefined,
      automationLevelId: undefined,
      // Custom-form-only fields, reset exactly as v1 resets them (§11's
      // `prefill.customBrandName/numberOfStations/system/version/...` row).
      customBrandName: undefined,
      numberOfStations: undefined,
      system: undefined,
      version: undefined,
      description: undefined,
      image: null,
    };

    try {
      const id = await dispatch(createMachine(payload)).unwrap();
      // The rejected reducer writes `error`, which MachineryToastHost renders.
      // On this surface the message belongs in the dialog the user is still
      // looking at, so the slice copy is cleared — otherwise the in-dialog
      // notice and a toast would say the same thing twice and the toast would
      // outlive the dialog. Same call the custom dialog makes.
      dispatch(setError(undefined));
      onCreated(id);
    } catch (rejected) {
      dispatch(setError(undefined));
      setSubmitError(
        toastErrorMessage(rejected, translate("myMachinery:v2.add.createFailed"))
      );
    } finally {
      setSubmitting(false);
    }
  };

  return {
    form,
    locations,
    locationId,
    setLocationId,
    submitting,
    submitError,
    resetAll,
    submit: form.handleSubmit(onSubmit),
  };
};

export default useCatalogAddForm;
